// STO-3295: Mobile sheet migrated from custom MobileSheet + SwipeHandle to MUI
// SwipeableDrawer. Swipe-to-dismiss, scroll lock, Escape handling, focus trap,
// and aria-modal are all provided by SwipeableDrawer's internal Modal wrapper.
import { styled } from '@mui/material/styles'
import Box from '@/components/common/Box'
import {
	colors,
	spacing,
	radius,
	shadows,
	focusVisible,
	breakpoints,
	zIndex,
} from '@/styles/designTokens'

// ---------------------------------------------------------------------------
// Desktop sidebar — fills the sticky wrapper height; items list scrolls inside.
// Visible only at lg+; breakpoint controlled by CartSidebarSticky in MenuLayout.
// ---------------------------------------------------------------------------
export const DesktopSidebar = styled(Box)({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	height: '100%',
	overflow: 'hidden',
	borderRadius: radius.sheet,
	border: `1px solid ${colors.border}`,
	boxShadow: shadows.card,
	backgroundColor: colors.surfaceWhite,
})

// ---------------------------------------------------------------------------
// Tablet — full-viewport dimmer; always in DOM, toggled via visibility.
// Click on dimmer closes the drawer; click on modal stops propagation.
// ---------------------------------------------------------------------------
export const TabletDimmer = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>(({ isOpen }) => ({
	position: 'fixed',
	inset: 0,
	backgroundColor: colors.overlayBackdrop,
	zIndex: zIndex.modal,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	visibility: isOpen ? 'visible' : 'hidden',
	pointerEvents: isOpen ? 'auto' : 'none',
}))

export const TabletModal = styled(Box)({
	width: '100%',
	maxWidth: 400,
	backgroundColor: colors.surfaceWhite,
	borderRadius: radius.sheet,
	padding: spacing.lg,
})

// Tablet bottom sticky bar — visible only when drawer is closed.
// Native <button> for correct keyboard/focus/screen reader behaviour.
export const TabletStickyBar = styled('button', {
	shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>(({ isOpen }) => ({
	position: 'fixed',
	bottom: 0,
	left: '50%',
	transform: 'translateX(-50%)',
	width: 400,
	backgroundColor: colors.surfaceWhite,
	borderRadius: radius.sheet,
	border: `1px solid ${colors.brand}`,
	paddingLeft: spacing.sm,
	paddingRight: spacing.sm,
	paddingTop: spacing.lg,
	paddingBottom: spacing.lg,
	cursor: 'pointer',
	zIndex: 1200, // MUI appBar z-index
	fontFamily: 'inherit',
	visibility: isOpen ? 'hidden' : 'visible',
	pointerEvents: isOpen ? 'none' : 'auto',
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.brand}`,
		outlineOffset: focusVisible.outlineOffset,
	},
}))

// ---------------------------------------------------------------------------
// Mobile — SwipeableDrawer paper styles and drag pill.
// ---------------------------------------------------------------------------

// SwipeableDrawer Paper styles — passed via slotProps.paper.sx so MUI applies them
// to the internal Paper element without wrapping it in a styled component.
export const drawerPaperSx = {
	backgroundColor: colors.surfaceWhite,
	borderRadius: `${radius.sheet}px ${radius.sheet}px 0 0`,
	maxHeight: '80vh',
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
	[`@media (min-width: ${breakpoints.desktopSm}px)`]: {
		width: 400,
		borderRadius: `${radius.sheet}px`,
		left: 0,
		right: 0,
		bottom: `${spacing.lg}px`,
		marginLeft: 'auto',
		marginRight: 'auto',
		transform: 'none',
	},
} as const

export { DrawerPuller } from '@/components/common/ui/DrawerPuller.styled'

// Full-width sticky bar — sits at the bottom when the mobile sheet is closed.
export const MobileStickyBar = styled('button')({
	position: 'fixed',
	bottom: 0,
	left: 0,
	right: 0,
	backgroundColor: colors.surfaceWhite,
	borderRadius: `${radius.sheet}px ${radius.sheet}px 0 0`,
	border: `1px solid ${colors.border}`,
	padding: spacing.lg,
	cursor: 'pointer',
	zIndex: 1200, // MUI appBar z-index
	fontFamily: 'inherit',
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.brand}`,
		outlineOffset: focusVisible.outlineOffset,
	},
})
